// ============================================================
// TwPopoverContext — parent↔child coordination (TWS-122 Wave 2)
// ------------------------------------------------------------
// Slot family:
//
//   <tw-popover>                 (parent — owns state + id/anchor-name)
//     <button twPopoverTrigger>  (attribute directive — must host
//                                 on a real <button> so native
//                                 `popovertarget` invoker
//                                 semantics fire)
//     <tw-popover-content>       (slot — hosts the `[popover]` panel)
//       <tw-popover-arrow>       (slot — optional pointer)
//
// Mirrors the Wave 1 dropdown shape but differs in two ways:
//   1. No focus-first-item semantics — popovers host arbitrary
//      dialog content and should not auto-shift focus onto any
//      particular element.
//   2. Exposes the current `triggerOn` mode so the trigger slot
//      can decide whether to wire click-via-popovertarget (native)
//      or hover/focus handlers.
// ============================================================

import { InjectionToken, Signal } from '@angular/core';

import { TwPopoverPlacement } from '../shared/types';

/**
 * Trigger activation mode. `click` uses the native
 * `popovertarget` attribute for zero-JS-open semantics; `hover`
 * wires mouseenter / focusin listeners with show/hide delays.
 */
export type TwPopoverTriggerMode = 'click' | 'hover';

/**
 * Shape the parent publishes through `TW_POPOVER_CONTEXT`.
 * Kept narrow — only what slot components consume. Expanding
 * this surface is a breaking change for out-of-tree popover
 * slot variants.
 */
export interface TwPopoverContext {
  /** Generated DOM id used for `popovertarget` ↔ `id` wiring. */
  readonly popoverId: string;
  /** Generated CSS custom-property name for anchor wiring. */
  readonly anchorName: string;
  /** Current open state (reactive — trigger reads for aria-expanded). */
  readonly open: Signal<boolean>;
  /** Placement hint — content + arrow read it via data attribute / signal. */
  readonly placement: Signal<TwPopoverPlacement>;
  /** Trigger activation mode — trigger slot branches on it. */
  readonly triggerOn: Signal<TwPopoverTriggerMode>;
  /**
   * Registered trigger element. Populated by the trigger slot
   * on `ngOnInit`; consumed by the content slot's CDK-fallback
   * branch to anchor the overlay. The native path does NOT
   * need this — it uses CSS `anchor-name` wiring on the DOM
   * directly.
   */
  readonly triggerElement: Signal<HTMLElement | null>;
  /** Called by content on native `toggle` event. */
  setOpen(value: boolean): void;
  /** Registers the trigger element so `anchor-name` CSS wiring can target it. */
  registerTriggerElement(el: HTMLElement): void;
  /**
   * Registers the trigger directive's content-hover handler so
   * the content slot can route its own `mouseenter` / `mouseleave`
   * back into the trigger's hover-state machine (TWS-108 Wave 8).
   * Pass `null` on teardown.
   */
  registerContentHoverHandler(handler: ((hovered: boolean) => void) | null): void;
  /**
   * Hover-bridge hook (TWS-108 Wave 8 regression fix).
   *
   * The content slot calls this on its own `mouseenter` /
   * `mouseleave` so the trigger directive's hover state machine
   * treats trigger + content as one logical hover region. Without
   * this, the cursor traversing the small gap between trigger and
   * panel (a `var(--twist-spacing-2)` margin from the anchor-
   * positioning mixin) would land in a dead zone and the trigger's
   * hide timer would fire mid-traversal.
   *
   * No-ops in `click` mode (and when no hover trigger has
   * registered) — the default implementation is a no-op so slots
   * mounted outside a hover-mode `<tw-popover>` do not need
   * special handling.
   */
  notifyContentHovered(hovered: boolean): void;
}

/**
 * DI token the parent component provides to its subtree. A slot
 * rendered outside a `<tw-popover>` parent is a consumer error;
 * Angular's missing-provider error surfaces that miswiring
 * precisely.
 */
export const TW_POPOVER_CONTEXT = new InjectionToken<TwPopoverContext>(
  'TW_POPOVER_CONTEXT',
);
